import { ReactNode, startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InfiniteData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { Box, IconButton, LinearProgress, Tooltip } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";

import Drawer from "./drawer";
import EndpointButtons from "./EndpointButtons";
import { useConfig } from "../../contexts/ConfigContext";
import { ApiEndpoint, Entity, FlexibleDataItem, MediaItemDetailed } from "../../types";
import SearchBar from "./search-bar";
import useAuth from "../../hooks/useAuth";

import DataGrid from "./data-grid/DataGrid";
import { fetchEndpointData } from "../../utilities/dataSourceAdapters";
import { useAuthSelector } from "../../store/auth";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import DataGridToolbar from "./data-grid/DataGridToolBar";
import { useUIActions, useUISelector } from "../../store/UI";
import RelationshipView from "./relationship-view/RelationshipView";
import { getCmTableData } from "../../services/cmTables";
import { isCmTableEndpoint, getCmTableSlugFromEndpoint } from "../../utilities/cmTableEndpoint";
import LayerSharePopover from "../share/LayerSharePopover";

import { useMapSelector, useMapActions, useMapStore } from "../../store/map";
import { isAlreadyFramedDataset } from "../../utilities/mapBounds";
import { isGeometryInViewport } from "../../utilities/geoUtils";
import { usePopupActions, usePopupSelector } from "../../store/popupStore";
import useResize from "../../hooks/useResize";
import {
  DATA_DRAWER_HEIGHT,
  DATA_DRAWER_MULTI_SOURCE_HEIGHT,
  MAX_DATA_DRAWER_WIDTH,
  MIN_DATA_DRAWER_WIDTH,
  RESIZING_ICON_OFFSET,
  RESIZING_ICON_TOP,
  SIDEBAR_TOGGLE_ICON_OFFSET
} from "../../constants/uiConstants";
import { useMediaActions } from "../../store/media";
import { useSelectionActions, useSelectionSelector } from "../../store/selection";
import { useRelationshipPanelActive } from "../../hooks/useSelectedEntity";
import { hasValidGeometry } from "../../utilities/geoUtils";
import { entityIsMedia } from "../../utilities/entityModalUtils";
import { useEntityFormActions } from "../../store/entityForm";
import { useLayerActions } from "../../store/layer";
import { getEntityCategory } from "../../utilities/common";
import { SpatialDataset } from "../../types";
import { toLayerConfig } from "../../hooks/useSpatialDatasetMapLayers";
import { spatialDatasetLayerId } from "../../constants/spatialDataset";
import { ASSET_POLL_INTERVAL_MS, isLiveAsset } from "../../services/assetStatus";
import { getSpatialDataset } from "../../services/spatialDatasetService";

const PAGE_SIZE = 10;

// Render actual data grid
function RenderDataGrid({
  data,
  searchTerm,
  currentEndpoint,
  isPending,
  isRefetching,
  error,
  onLoadMore,
  hasMoreDataToFetch
}: {
  data: InfiniteData<Awaited<ReturnType<typeof fetchEndpointData>> | undefined, unknown> | undefined;
  searchTerm: string;
  currentEndpoint: ApiEndpoint;
  onServerSearchFinish?: () => void;
  onServerSearchInit?: boolean;
  isPending?: boolean;
  isRefetching?: boolean;
  error?: unknown;
  onLoadMore?: () => void;
  hasMoreDataToFetch?: boolean;
}) {
  const totalResultCount = useRef(0);
  const [filteredItemCount, setFilteredItemCount] = useState<number | null>(null);
  const { dataGridViewMode } = useUISelector();
  const [searchParams] = useSearchParams();
  const { setEditItems } = useMediaActions();
  const navigate = useNavigate();
  const config = useConfig();
  const { selectedEndpoint } = useSelectionSelector();
  const { setSelectedPrimaryStates, setSecondaryItems, clearSecondaryItems } = useSelectionActions();
  const { openGeoPopup, openMediaViewer, openNonGeoPopup } = usePopupActions();
  const { openCreateForm } = useEntityFormActions();
  const { refreshLayer, mergeSpatialDatasetLayers, setLayerVisibility } = useLayerActions();
  const { setCommonMapState } = useMapActions();
  // Which dataset the map is already showing, so re-opening its card doesn't yank
  // the camera while picking a different one always frames it. See the flyTo
  // handler in MapComponent.
  const activeSpatialDatasetEntityId = useMapStore((s) => s.activeSpatialDatasetEntityId);
  const { canCreate } = useAuth();

  // Cache total result count
  const resultCount = useMemo(() => {
    if (isPending || isRefetching) return totalResultCount.current;
    const result = data && data.pages?.[0];
    if (result) {
      totalResultCount.current = result?.pagination ? result?.pagination?.count : result?.data?.length;
    } else {
      totalResultCount.current = 0;
    }
    return totalResultCount.current;
  }, [data, isPending, isRefetching]);

  // Show error if fetch failed
  useEffect(() => {
    if (error) {
      enqueueUniqueSnackbar(enqueueSnackbar, `Error loading ${currentEndpoint!.name}`, {
        variant: "error"
      });
    }
  }, [error, currentEndpoint]);

  const handleSelectEntity = (entity: FlexibleDataItem) => {
    setSelectedPrimaryStates({
      selectedEndpoint: currentEndpoint,
      selectedEntityId: entity.id,
      showRelationShip: false // Always collapse relationship panel on new selection
    });

    // Spatial datasets: open the corner detail card (same non-geo variant as other
    // entities), fetch full detail (list items lack display_url / bbox), register
    // the COG/PMTiles layer, and fly to bounds. No pin marker — the layer is the
    // map representation. Single-active model: one overlay at a time (§6e decision).
    if (currentEndpoint?.entityType === "spatial-dataset") {
      openNonGeoPopup();
      (async () => {
        setCommonMapState({ isSpatialDatasetLoading: true });
        let flyToDispatched = false;
        try {
          const ds: SpatialDataset = await getSpatialDataset(Number(entity.id));

          if (ds.processing_status === "completed" && ds.display_url) {
            const layerConfig = toLayerConfig(ds);
            if (layerConfig) {
              mergeSpatialDatasetLayers([layerConfig]);
              setLayerVisibility(spatialDatasetLayerId(ds.id), true);
            }
          }

          if (ds.bbox && ds.bbox.length === 4) {
            const [minLng, minLat, maxLng, maxLat] = ds.bbox;
            window.dispatchEvent(
              new CustomEvent("culturemap:flyTo", {
                detail: {
                  layerId: spatialDatasetLayerId(ds.id),
                  bounds: [
                    [minLng, minLat],
                    [maxLng, maxLat]
                  ],
                  clearSpatialLoading: true,
                  // Only a dataset already on the map can be "already framed";
                  // a newly picked one is always framed, however wide its extent.
                  skipIfCenterWithinBounds: isAlreadyFramedDataset(activeSpatialDatasetEntityId, entity.id)
                }
              })
            );
            flyToDispatched = true;
          }
        } catch {
          // Non-fatal — if detail fetch fails the map just doesn't update
        }
        if (!flyToDispatched) {
          setCommonMapState({ isSpatialDatasetLoading: false });
        }
      })();
      return;
    }

    if (hasValidGeometry(entity as Entity)) {
      openGeoPopup();
    } else {
      if (entityIsMedia(entity as Entity)) {
        openMediaViewer();
      } else {
        openNonGeoPopup();
      }
    }
  };

  // *************************

  const flattenData = useMemo(() => {
    return data?.pages?.flatMap((page) => page?.data as FlexibleDataItem[]) || [];
  }, [data?.pages]);

  const { cmTableBbox, isMapBboxDebouncing } = useMapSelector();
  const isGeoEndpoint = currentEndpoint?.entityType === "geo";

  // Global setting, configurable in Application Settings (defaults from config.json).
  const limitToExtent = config.limitDataDrawerToExtent ?? true;
  const extentActive = isGeoEndpoint && !!cmTableBbox && limitToExtent;

  // Sort visible items to top and build the visibleIds set for greying.
  // cmTableBbox is already debounced (500 ms) by useCmTableBbox in MapComponent.
  const { sortedData, visibleIds } = useMemo(() => {
    if (!extentActive || flattenData.length === 0) {
      return { sortedData: flattenData, visibleIds: undefined };
    }
    const [minLng, minLat, maxLng, maxLat] = cmTableBbox!.split(",").map(Number);
    const visible: FlexibleDataItem[] = [];
    const notVisible: FlexibleDataItem[] = [];
    const ids = new Set<string | number>();
    for (const item of flattenData) {
      if (isGeometryInViewport((item as any).geometry, minLng, minLat, maxLng, maxLat)) {
        visible.push(item);
        ids.add(item.id);
      } else {
        notVisible.push(item);
      }
    }
    return { sortedData: [...visible, ...notVisible], visibleIds: ids };
  }, [flattenData, cmTableBbox, extentActive]);

  // add to secondary selection on search
  const searchQuery = searchParams.get("search"); // this search is implemented to apply search highlight only when pressed enter
  useEffect(() => {
    if (!searchQuery || flattenData.length === 0) {
      clearSecondaryItems();
      return;
    }

    setSecondaryItems(
      flattenData.map((item) => ({
        id: item.id,
        layerId: currentEndpoint?.mapLayerId ?? ""
      })),
      "search"
    );
  }, [flattenData, searchQuery, currentEndpoint?.mapLayerId, setSecondaryItems, clearSecondaryItems]);

  function onHandleBulkEditSelection() {
    if (flattenData?.length) {
      setEditItems(flattenData as MediaItemDetailed[]);

      startTransition(() => {
        navigate(`/editor?endpoint=${currentEndpoint?.tableName}`);
      });
    }
  }

  const handleFilterChange = useCallback(
    (matchedFilteredCount: number, _hiddenItem: number) => {
      if (!searchTerm || !searchTerm?.trim() || !searchQuery || !searchQuery?.trim()) {
        setFilteredItemCount(null);
      }
      if (searchTerm || searchQuery) {
        setFilteredItemCount(matchedFilteredCount);
      }
    },
    [searchTerm, searchQuery, setFilteredItemCount]
  );

  const currentResultCount = filteredItemCount !== null && filteredItemCount >= 0 ? filteredItemCount : flattenData?.length;

  const shouldShowBulkEditTrigger = Boolean(searchQuery) && flattenData && flattenData?.length > 0 && flattenData?.length <= 100;

  // find whether application has multiple data source
  // on multiple data source reduce the height of
  // data drawer from overlapping infinite scroll behaviour
  const hasMultipleDataSource = useMemo(() => {
    let isMultiEndpoint = false;
    let dataSourceName: string | null = null;

    const apiEndpionts = config?.apiEndpoints;
    if (!apiEndpionts?.length) return isMultiEndpoint;

    apiEndpionts?.forEach((ep) => {
      if (dataSourceName && ep.dataSourceType && ep?.dataSourceType !== dataSourceName) {
        isMultiEndpoint = true;
        return;
      }

      // if data source is not defined explicitly
      // define `default` value as source, so
      // we can compare default with the explicitly defined one
      if (dataSourceName || !dataSourceName) {
        dataSourceName = ep?.dataSourceType || "default";
      }
    });

    return isMultiEndpoint;
  }, [config?.apiEndpoints?.length]);

  const handleAddEntity = () => {
    if (selectedEndpoint) {
      openCreateForm(selectedEndpoint, {
        onUpdated(entity) {
          const entityType = getEntityCategory(entity);
          if (entityType === "geo") {
            // select as current entity
            setSelectedPrimaryStates({ selectedEntityId: entity.id });

            const layerId = selectedEndpoint?.mapLayerId || selectedEndpoint.tableName;
            if (layerId) {
              refreshLayer(layerId);
            }
          }
        }
      });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <Box sx={{ px: "1rem" }}>
        {/* Sidebar/Datagrid toolbar*/}
        <DataGridToolbar
          title={currentEndpoint ? `${currentEndpoint?.name}` : ""}
          currentResultCount={`Showing ${currentResultCount || 0}`}
          onBulkEdit={onHandleBulkEditSelection}
          totalResult={resultCount}
          showBulkEdit={shouldShowBulkEditTrigger}
          onAdd={handleAddEntity}
          showAddOption={
            canCreate &&
            selectedEndpoint?.tableName !== "media" &&
            selectedEndpoint?.entityType !== "spatial-dataset" &&
            selectedEndpoint?.dataSourceType !== "fulcrum"
          }
          selectedEndpointName={selectedEndpoint?.name || ""}
          actions={
            // Layer-tier share: no `rowIds` means the whole table. The guard is
            // the same pair `DataItemDetails` uses for its record-tier mount, so
            // the two surfaces never disagree about which tables are shareable.
            isCmTableEndpoint(currentEndpoint) && currentEndpoint?.spatialType !== "none" ? (
              // `size`/`iconSx` left to match the neighbouring toolbar buttons.
              <LayerSharePopover shareContext={{ kind: "cm-table", endpoint: currentEndpoint }} iconSx={{ px: 2 }} />
            ) : null
          }
        />
      </Box>

      {extentActive && isMapBboxDebouncing && <LinearProgress sx={{ mx: "1rem", borderRadius: 1, height: 2 }} />}

      <DataGrid
        data={sortedData || []}
        onSelect={handleSelectEntity}
        compact={false}
        currentEndpoint={currentEndpoint}
        searchTerm={searchTerm}
        viewMode={dataGridViewMode === "list" ? "table" : "cards"}
        isPending={isPending}
        hasMoreData={hasMoreDataToFetch}
        onLoadMore={onLoadMore}
        isMoreLoading={isRefetching}
        onFilteredCountChange={handleFilterChange}
        viewportVisibleIds={visibleIds}
        sx={{ height: hasMultipleDataSource ? DATA_DRAWER_MULTI_SOURCE_HEIGHT : DATA_DRAWER_HEIGHT }}
      />
    </Box>
  );
}

// Sidebar content to render Search bar and data grid component
function SidebarContent() {
  // searching/filter value for frontend search
  const [filterValue, setFilterValue] = useState("");

  const [searchParams, setSearchParams] = useSearchParams({ page: "1" });

  // ***************
  const { setSelectedPrimaryStates } = useSelectionActions();
  const { selectedEndpoint: selectedApiEndpoint } = useSelectionSelector();
  const { isOpen } = usePopupSelector();
  const { closePopup } = usePopupActions();
  const { setPendingFlyTo, setCoordinateSearchMarker } = useMapActions();
  // ***************

  const config = useConfig();
  const { user } = useAuthSelector();
  const { canManageRestrictions } = useAuth();

  // CM Table endpoints are now derived from config.apiEndpoints.
  // "restrictions" is a management-only endpoint — hide it for users without the
  // manage_restrictions permission so they never see the tab or its data.
  const allEndpoints = useMemo(
    () => (config.apiEndpoints ?? []).filter((ep) => ep.tableName !== "restrictions" || canManageRestrictions),
    [config.apiEndpoints, canManageRestrictions]
  );

  const queryClient = useQueryClient();

  const searchTerm = searchParams.get("search"); // searching value for backend search

  const [, setTick] = useState(0);
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event?.type === "updated" &&
        Array.isArray(event.query.queryKey) &&
        event.query.queryKey[0] === "drawer-data" &&
        event.query.queryKey[1] === selectedApiEndpoint?.tableName
      ) {
        setTick((t) => t + 1);
      }
    });
    return unsubscribe;
  }, [selectedApiEndpoint?.tableName, queryClient]);

  // CM Table endpoints are identified by the central utility
  const isCmTableEndpointDetected = isCmTableEndpoint(selectedApiEndpoint);

  const { data, isPending, isFetchingNextPage, isError, error, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["drawer-data", selectedApiEndpoint?.tableName, searchTerm],
    queryFn: async ({ pageParam = 1 }) => {
      // CM Table data source – use the v2 nested slug-based API
      if (isCmTableEndpointDetected) {
        const tableSlug = getCmTableSlugFromEndpoint(selectedApiEndpoint);
        if (tableSlug) {
          const res = await getCmTableData(tableSlug, {
            page: pageParam as number,
            page_size: PAGE_SIZE,
            ...(searchTerm ? { search: searchTerm } : {})
          });
          const apiData = res.data;
          return {
            data: apiData.results,
            pagination: {
              count: apiData.count,
              next: apiData.next,
              previous: apiData.previous
            }
          };
        }
      }

      // All other endpoints (including media) use the api_endpoint from config
      const result = await fetchEndpointData(selectedApiEndpoint!, user, {
        filters: {
          page: pageParam?.toString() as string,
          page_size: PAGE_SIZE.toString(),
          ...(searchTerm ? { search: searchTerm } : {})
        }
      });
      return result;
    },
    initialPageParam: 1,
    getNextPageParam: (page) => {
      if (page.pagination && page.pagination?.next) {
        try {
          const url = new URL(page.pagination.next);
          const nextCursor = url.searchParams.get("page");
          if (!nextCursor) return undefined;
          return Number(nextCursor);
        } catch {
          return undefined;
        }
      }
      return undefined;
    },
    enabled: Boolean(selectedApiEndpoint?.tableName),
    // §6b / §6c: Poll while any spatial dataset row is queued or converting
    // (the same rule as the detail card's poll). Stops once no row is live.
    refetchInterval: (queryResult) => {
      if (selectedApiEndpoint?.entityType !== "spatial-dataset") return false;
      const pages = (queryResult.state.data as InfiniteData<any> | undefined)?.pages ?? [];
      const rows: any[] = pages.flatMap((p: any) => p.data ?? []);
      return rows.some(isLiveAsset) ? ASSET_POLL_INTERVAL_MS : false;
    }
  });

  function handleLoadMoreData() {
    if (isFetchingNextPage || isPending || !hasNextPage) return;
    fetchNextPage();
  }

  // Sync local filter/search value from url
  useEffect(() => {
    setFilterValue(searchTerm || "");
  }, [searchTerm]);

  // Show error if fetch failed
  useEffect(() => {
    if (isError && error) {
      enqueueUniqueSnackbar(enqueueSnackbar, `Error loading ${selectedApiEndpoint!.name}`, {
        variant: "error"
      });
    }
  }, [isError, error, selectedApiEndpoint]);

  const updateSearchQueryParams = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams);
      params.set(key, value);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const deleteSearchQueryParams = useCallback(
    (key: string | string[]) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (Array.isArray(key)) {
          key.forEach((paramKey) => {
            params.delete(paramKey);
          });
        } else {
          params.delete(key);
        }
        return params;
      });
    },
    [setSearchParams]
  );

  function handleSearchFilter(value: string) {
    if (!value || !value?.trim()) {
      deleteSearchQueryParams("search");
    }

    setFilterValue(value);
  }

  function handleSearchOnEnter(_?: KeyboardEvent, value?: string) {
    if (isPending) return;

    if (!value) {
      deleteSearchQueryParams("search");
      return;
    }

    // --- Coordinate interception (decimal or DMS) ---

    // Decimal: "lat, lng" or "lat,lng"
    if (value.includes(",")) {
      const parts = value.split(",").map((p) => parseFloat(p.trim()));
      if (parts.length === 2 && !parts.some(isNaN)) {
        const [lat, lng] = parts;
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          setPendingFlyTo({ lng, lat, zoom: 14 });
          setCoordinateSearchMarker({ lng, lat });
          enqueueSnackbar(`Flying to ${lat.toFixed(5)}, ${lng.toFixed(5)}`, { variant: "info", autoHideDuration: 2500 });
          return;
        } else {
          enqueueSnackbar("Invalid coordinates: lat must be −90..90, lng must be −180..180", { variant: "error" });
          return;
        }
      }
    }

    // DMS: e.g. 27°41'37.4"N 85°20'52.2"E  (° ' " and NSEW required)
    const dmsTokens = [...value.matchAll(/(\d+(?:\.\d+)?)\s*°\s*(\d+(?:\.\d+)?)\s*['\u2019]\s*(\d+(?:\.\d+)?)\s*["\u201d]?\s*([NSEW])/gi)];
    if (dmsTokens.length === 2) {
      const toDec = (m: RegExpMatchArray) => {
        const d = parseFloat(m[1]) + parseFloat(m[2]) / 60 + parseFloat(m[3]) / 3600;
        return /[SW]/i.test(m[4]) ? -d : d;
      };
      const lat = toDec(dmsTokens[0]);
      const lng = toDec(dmsTokens[1]);
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        setPendingFlyTo({ lng, lat, zoom: 14 });
        setCoordinateSearchMarker({ lng, lat });
        enqueueSnackbar(`Flying to ${lat.toFixed(5)}, ${lng.toFixed(5)}`, { variant: "info", autoHideDuration: 2500 });
        return;
      }
    }

    // --- End coordinate interception ---

    updateSearchQueryParams("search", value);
  }

  function onHandleServerSearch(value?: string) {
    if (isPending) return;

    if (!value?.trim()) {
      deleteSearchQueryParams("search");
      return;
    }
    updateSearchQueryParams("search", value);
  }

  function handleClearSearch() {
    // Cancel search query immediately
    queryClient.cancelQueries({ queryKey: ["drawer-data"] });

    deleteSearchQueryParams("search");
    setFilterValue("");
  }

  const handleEndpointChange = (endpoint: ApiEndpoint) => {
    const paramsDeleteKeys: string[] = [];
    setSelectedPrimaryStates({
      selectedEndpoint: endpoint,
      selectedEntityId: null,
      showRelationShip: false // Collapse relationship panel when switching endpoints
    });

    if (selectedApiEndpoint?.tableName?.toLowerCase() !== endpoint?.tableName?.toLowerCase()) {
      // clear search
      if (searchTerm?.trim()) {
        paramsDeleteKeys.push("search");
      }
      if (filterValue?.trim()) {
        setFilterValue("");
      }

      // close previously open entity popup if entity isn't belong to new endpoint
      if (isOpen) {
        closePopup();
      }

      // clear popup query param if popup is not open but popup query is present
      if (!isOpen && searchParams.get("popup")?.trim()) {
        paramsDeleteKeys?.push("popup");
      }

      // Delete params that are registered
      if (paramsDeleteKeys?.length) {
        deleteSearchQueryParams(paramsDeleteKeys);
      }
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 16, pt: "1rem" }}>
      <Box sx={{ px: "1rem" }}>
        <SearchBar
          value={searchTerm || filterValue || ""}
          onChange={handleSearchFilter}
          placeholder='Search...'
          actions={{ onClear: handleClearSearch }}
          onServerSearch={onHandleServerSearch}
          showServerSearch={true}
          serverSearchLoading={isPending}
          searchOnEnter={handleSearchOnEnter}
          forceCleanup={!(searchTerm?.trim() || filterValue?.trim())}
        />
      </Box>

      <EndpointButtons
        endpoints={allEndpoints}
        currentEndpoint={selectedApiEndpoint}
        onEndpointChange={handleEndpointChange}
        // disabled={!!selectedItem}
      />

      <RenderDataGrid
        data={data}
        isPending={isPending}
        error={isError && error}
        searchTerm={filterValue || ""}
        currentEndpoint={selectedApiEndpoint as ApiEndpoint}
        onLoadMore={handleLoadMoreData}
        hasMoreDataToFetch={hasNextPage}
        isRefetching={isFetchingNextPage}
      />
    </Box>
  );
}

function RenderRelationshipView({ children }: { children: ReactNode }) {
  // Panel visibility tracks showRelationShip, which is set either by a manual
  // "Find Related" click or by the alwaysRequestRelatedItemsAndMedia auto-open in
  // useSelectedEntity. A manual close ("Back to grid") keeps it shut until the
  // next selection.
  const relationshipPanelActive = useRelationshipPanelActive();

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 2,
        background: "#1D2939",
        height: "100%",
        width: "100%",
        transform: `translateX(${relationshipPanelActive ? 0 : -100}%)`,
        transition: "400ms linear"
      }}
    >
      {children}
    </Box>
  );
}

export default function Sidebar({ calendarOffset = 0 }: { calendarOffset?: number }) {
  const { showSidebar, sidebarWidth } = useUISelector();
  const { toggleSidebar, setSidebarWidth } = useUIActions();
  const { width, enableResize } = useResize({
    minWidth: MIN_DATA_DRAWER_WIDTH,
    maxWidth: MAX_DATA_DRAWER_WIDTH,
    width: sidebarWidth,
    setWidth: setSidebarWidth
  });

  function handleToggleSidebar() {
    toggleSidebar();
  }

  return (
    <Box sx={{ position: "relative", width: showSidebar ? width : 0, height: "100%" }}>
      <Drawer
        isFullscreenMode={false}
        disableScrollLock={true}
        isOpen={showSidebar}
        sx={{
          maxWidth: "fit-content",
          zIndex: 1,
          // Hide backdrop overlay because sidebar is rendering standalone on the page
          // with absolute positioned
          "& .MuiBackdrop-root": {
            display: "none"
          }
        }}
        PaperProps={{
          sx: {
            maxWidth: "fit-content",
            position: "relative"
          }
        }}
        isCompactMode
        style={{
          top: calendarOffset,
          transition: "top 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: `${width}px`,
            height: `calc(100dvh - 4.1rem - ${calendarOffset}px)`,
            transition: "height 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            background: "#1D2939",
            left: 0,
            top: 0,
            zIndex: 999,
            overflow: "hidden"
          }}
        >
          <SidebarContent />

          {/* Render relationship of selected entity */}
          <RenderRelationshipView>
            <RelationshipView />
          </RenderRelationshipView>
        </Box>
      </Drawer>
      {showSidebar && (
        <IconButton
          sx={{
            position: "absolute",
            right: RESIZING_ICON_OFFSET,
            top: RESIZING_ICON_TOP,
            background: "#934872",
            zIndex: 999,
            paddingLeft: 6,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            "&:hover": {
              backgroundColor: "#9348727d"
            },
            pointerEvents: "auto",
            cursor: "ew-resize",
            touchAction: "none"
          }}
          onMouseDown={enableResize}
          onTouchMove={enableResize}
        >
          <Tooltip title='Resize sidebar'>
            <OpenInFullOutlinedIcon sx={{ color: "#fff", fontSize: "1.375rem" }} />
          </Tooltip>
        </IconButton>
      )}

      <IconButton
        sx={{
          position: "absolute",
          right: SIDEBAR_TOGGLE_ICON_OFFSET,
          top: "17%",
          backgroundColor: "#934872",
          color: "common.white",
          borderBottomLeftRadius: 0,
          borderTopLeftRadius: 0,
          paddingLeft: 0,
          "&:hover": {
            backgroundColor: "#9348727d"
          },
          pointerEvents: "auto",
          zIndex: 999
        }}
        onClick={handleToggleSidebar}
      >
        <Tooltip title='Toggle sidebar'>{!showSidebar ? <ChevronRight fontSize='large' /> : <ChevronLeft fontSize='large' />}</Tooltip>
      </IconButton>

      <Box
        sx={{
          position: "absolute",
          pointerEvents: "auto",
          height: "100%",
          width: "0.125rem",
          bgcolor: "grey.500",
          right: -1,
          top: 0,
          cursor: "col-resize",
          transition: "300ms ease-out",
          zIndex: 999,
          "&:hover": {
            bgcolor: "grey.400",
            boxShadow: "0 0 24px 0.5px",
            color: "primary.main"
          },
          "&:active": {
            bgcolor: "primary.main",
            width: "0.175rem"
          }
        }}
        onMouseDown={enableResize}
        onTouchMove={enableResize}
      />
    </Box>
  );
}
